<template>
  <div>
    <div
      class="px-4 py-3.5 border-b"
      :class="mode === 'dark' ? 'border-dark-50' : ''"
    >
      <div class="flex justify-between gap-2 space-3 xs:flex-col">
        <FilterByProjectForm></FilterByProjectForm>
        <ReportFilter :mode="mode" @toggle="$emit('toggle')"></ReportFilter>
        <div class="flex items-center gap-2">
          <button
            class="button primary-button"
            :class="[
              !showIsArchived
                ? 'opacity-50 pointer-events-none'
                : 'opacity-100',
            ]"
            @click="changeShowIsArchived(false)"
          >
            <CircleCheck :size="20" :stroke-width="1.75" />
            Active
          </button>
          <button
            class="button primary-button"
            :class="[
              showIsArchived ? 'opacity-50 pointer-events-none' : 'opacity-100',
            ]"
            @click="changeShowIsArchived(true)"
          >
            <Package :size="20" :stroke-width="1.75" />
            Archived
          </button>
          <button
            :class="[
              'button primary-button',
              { active: isShowOutdatedReports },
            ]"
            @click="loadReportsToggle"
            v-tooltip="{
              content: `<div style='max-width: 350px'>${getTooltipLabel}</div>`,
              html: true,
            }"
          >
            <FileChartColumnIncreasing :size="20" :stroke-width="1.75" />
            {{
              isShowOutdatedReports
                ? "Show All Reports"
                : "Show Outdated Reports"
            }}
          </button>
        </div>
      </div>
    </div>
    <div class="p-4 border-b" :class="mode === 'dark' ? 'border-dark-50' : ''">
      <h3 class="font-medium text-[17px] leading-none mb-2">Reports</h3>
      <p
        class="text-[15px] leading-none"
        :class="mode === 'dark' ? 'text-dark-15' : 'text-gray-600'"
      >
        Total {{ totalReport }}
        {{ totalReport > 1 ? "results" : "result" }} found
      </p>
    </div>
    <div
      class="w-full overflow-x-auto without-scrollbar max-h-screen overflow-y-auto"
    >
      <table class="w-full form-table">
        <thead
          class="border-b-2 sticky top-0 z-20"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <tr :class="mode === 'dark' ? 'bg-dark-1' : 'bg-gray-1'">
            <th @click="shortData('query')">
              <span class="flex items-center gap-1.5">
                <DataTableSortComponent
                  title="Search Query"
                  :mode="mode"
                ></DataTableSortComponent>
              </span>
            </th>
            <th>URL</th>
            <th>
              <span
                class="inline-block"
                v-tooltip="{
                  content: `This is the page's most recent SERP ranking that we saw, alongside any change from <br/> its last position. Moving from position 5 to position 2 is a gain of 3 spots`,
                  html: true,
                }"
                >Position</span
              >
            </th>
            <th>AI Article</th>
            <th>
              <span
                class="inline-block"
                v-tooltip="
                  `Overall Spam Score of your URL, title tag, meta description, H1 and body content`
                "
                >Spam Score</span
              >
            </th>
            <th>
              <span class="inline-block" v-tooltip="`Google Semantic AI Score`"
                >Semantic Score</span
              >
            </th>
            <th>
              <span
                class="inline-block"
                v-tooltip="
                  `HEEAT Score: Assessing Helpfulness, Expertise, Experience, Authority, Trust of Your Content`
                "
                >HEEAT</span
              >
            </th>
            <th>
              <span
                class="inline-block"
                v-tooltip="`Average Predictable Length`"
                >AvgPL</span
              >
            </th>
            <th>
              <span
                class="inline-block"
                v-tooltip="
                  `Difficulty is the overall Keyword Difficulty compared to all keywords`
                "
                >Difficulty</span
              >
            </th>
            <th>
              <span
                class="inline-block"
                v-tooltip="{
                  content: `<div style='width: 400px'>
                          <div>
                            The Keyword Revenue Ballpark (KRB) is an estimated figure, derived from the Cost Per Click (CPC), an ROI ratio based on Google Ads Competition as follows; Low = 2:1 Mid = 3:1 High = 4:1, and Year Over Year (YOY) traffic changes. A negative YOY indicates reduced traffic, impacting this estimate. The formula also includes 30% of 5% of the adjusted traffic.
                          </div>
                          <div class='mt-4'>
                            This figure is a rough approximation for illustrative purposes, based on market trends and historical data, and may not reflect current or future market conditions. It's intended as a guideline for potential earnings, not a guarantee of actual revenue.
                          </div>
                        </div>`,
                  html: true,
                }"
                >KRB</span
              >
            </th>
            <th @click="shortData('project.name')">
              <span class="flex items-center gap-1.5">
                <DataTableSortComponent
                  title="Project"
                  :mode="mode"
                ></DataTableSortComponent>
              </span>
            </th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody
          class="divide-y bg-opacity-40"
          :class="
            mode === 'dark'
              ? 'bg-dark-70 divide-dark-50'
              : 'bg-white-100 divide-gray-100'
          "
        >
          <template v-for="(report, index) in filteredReports" :key="index">
            <tr class="report-body-tr">
              <td>
                <div class="max-w-[200px]">
                  <div class="flex items-center">
                    <span
                      v-if="
                        isReportFailed(report) || isReportProcessing(report)
                      "
                      class="font-medium block text-wrap max-w-fit"
                      v-tooltip="{
                        content: `<div style='width: 350px'>Query: ${
                          report.query
                        } <br />SERP Limit: ${report.result_count}
                        </br />
                        Date: ${fromNow(report.created_at)}
                        </br />
                          ${getSearhEngine(report)}
                        </div>`,
                        html: true,
                      }"
                      >{{ report.query }}</span
                    >
                    <RouterLink
                      v-else
                      :to="{
                        name: 'editor',
                        params: { uuid: report.uuid },
                      }"
                      class="hover:text-primary-100 font-medium block text-wrap max-w-fit"
                      v-tooltip="{
                        content: `<div style='width: 350px'>Query: ${
                          report.query
                        } <br />
                        SERP Limit: ${report.result_count}
                        </br />
                        Date: ${fromNow(report.created_at)}
                        </br />
                          ${getSearhEngine(report)}
                        </div>`,
                        html: true,
                      }"
                    >
                      {{ report.query }}
                    </RouterLink>
                    <div class="ml-2 flex items-center gap-0.5">
                      <span
                        @click="copyToBoard(report.query)"
                        class="cursor-pointer"
                        v-tooltip="`Copy to clipboard`"
                      >
                        <Clipboard :size="16" :stroke-width="2" />
                      </span>
                      <a
                        target="_blank"
                        :href="`https://www.google.com/search?gl=${
                          report?.country
                        }&hl=${report?.location}&pws=0&q=${encodeURIComponent(
                          report.query
                        )}`"
                        v-tooltip="`View SERP`"
                      >
                        <ExternalLink :size="16" :stroke-width="2" />
                      </a>
                    </div>
                  </div>
                  <div
                    v-if="
                      !isReportFailed(report) && !isReportProcessing(report)
                    "
                    class="flex items-center gap-1"
                  >
                    <RouterLink
                      :to="{
                        name: 'serp-report-ta',
                        params: { uuid: report.uuid },
                      }"
                      target="_blank"
                      class="cursor-pointer hover:text-primary-100 text-primary-100"
                    >
                      <span
                        v-tooltip="{
                          content: `<div style='width: 330px'>Check keyword results of the AI Models & Tests performed for your EMQ</div>`,
                          html: true,
                        }"
                      >
                        Report
                      </span>
                    </RouterLink>
                    <span>|</span>
                    <RouterLink
                      :to="{
                        name: 'editor',
                        params: { uuid: report.uuid },
                      }"
                      target="_blank"
                      class="cursor-pointer hover:text-primary-100 text-primary-100"
                    >
                      <span
                        v-tooltip="{
                          content: `<div style='width: 330px'>
                                Use our Article Builder to Generate / Optimize all your SEO content
                              </div>`,
                          html: true,
                        }"
                      >
                        Article Builder
                      </span>
                    </RouterLink>
                  </div>
                </div>
              </td>
              <td>
                <template v-if="report.track_url">
                  <div class="flex items-center gap-1">
                    <div
                      v-if="
                        urlTrackReportId[index] &&
                        report.uuid === urlTrackReportId[index]
                      "
                    >
                      <ChevronDown
                        :size="18"
                        :stroke-width="1.75"
                        v-if="isExpandTrackUrlDetails[index]"
                        @click="isExpandTrackUrlDetails[index] = false"
                        class="cursor-pointer"
                      />
                      <ChevronRight
                        :size="18"
                        :stroke-width="1.75"
                        v-else
                        @click="isExpandTrackUrlDetails[index] = true"
                        class="cursor-pointer"
                      />
                    </div>
                    <ChevronRight
                      :size="18"
                      :stroke-width="1.75"
                      v-else
                      @click="loadRankDetails(report.uuid, index)"
                      class="cursor-pointer"
                      v-tooltip="`Organic Keywords`"
                    />
                    <div
                      class="overflow-ellipsis max-w-[80px] overflow-hidden"
                      v-tooltip="report.track_url"
                    >
                      <a
                        :href="handleUrlOpen(report.track_url)"
                        target="_blank"
                      >
                        {{ report.track_url }}
                      </a>
                    </div>
                    <span
                      @click="handleTrackingModal(report)"
                      class="cursor-pointer"
                    >
                      <Pencil :size="14" :stroke-width="2" />
                    </span>
                  </div>
                  <div class="flex items-center mt-0.5">
                    <div class="checkbox">
                      <input
                        :id="report.uuid"
                        type="checkbox"
                        :checked="
                          report.track_url_status === 20 ||
                          report.track_url_status === 30
                        "
                        :value="report.track_url_status"
                        @change="handleTrackURLChange($event, report)"
                      />
                      <label :for="report.uuid">Rank tracking</label>
                    </div>
                  </div>
                </template>
                <button
                  v-if="!report.track_url"
                  class="h-8 px-2 bg-primary-100 text-white-100 rounded-md"
                  v-tooltip="
                    `Monitor your rankings over time and track your performance`
                  "
                  @click="handleTrackingModal(report)"
                >
                  <Link :size="16" :stroke-width="1.75" />
                </button>
              </td>
              <td>
                <template v-if="report.track_url_status == 30"
                  >Not ranking</template
                >
                <template
                  v-else-if="
                    report.ranktracker &&
                    report.ranktracker.length > 0 &&
                    report.ranktracker.filter((p) => p.position).length
                  "
                >
                  <div class="flex items-center gap-1.5">
                    <span>{{
                      getSortedTrackings(report.ranktracker)[0]["position"] < 0
                        ? "100+"
                        : getSortedTrackings(report.ranktracker)[0]["position"]
                    }}</span>
                    <span
                      v-if="
                        getSortedTrackings(report.ranktracker)[0][
                          'position'
                        ] === -1 &&
                        report.ranktracker.length > 1 &&
                        getSortedTrackings(report.ranktracker)[1][
                          'position'
                        ] === -1
                      "
                    >
                      -
                    </span>
                    <span
                      class="flex items-center gap-0.5"
                      v-if="
                        getSortedTrackings(report.ranktracker)[0]['position'] >
                          -1 &&
                        report.ranktracker.length > 1 &&
                        getSortedTrackings(report.ranktracker)[1]['position'] >
                          -1
                      "
                      :class="[
                        getSortedTrackings(report.ranktracker)[0]['position'] >
                        getSortedTrackings(report.ranktracker)[1]['position']
                          ? 'text-danger-100'
                          : getSortedTrackings(report.ranktracker)[0][
                              'position'
                            ] <
                              getSortedTrackings(report.ranktracker)[1][
                                'position'
                              ] ||
                            (getSortedTrackings(report.ranktracker)[0][
                              'position'
                            ] !==
                              getSortedTrackings(report.ranktracker)[1][
                                'position'
                              ] &&
                              getSortedTrackings(report.ranktracker)[0][
                                'position'
                              ] *
                                getSortedTrackings(report.ranktracker)[1][
                                  'position'
                                ] <
                                0)
                          ? 'text-success-100'
                          : '',
                      ]"
                    >
                      {{
                        Math.abs(
                          getSortedTrackings(report.ranktracker)[0][
                            "change_in_position"
                          ]
                        ) || "-"
                      }}
                      <ArrowDown
                        v-if="
                          getSortedTrackings(report.ranktracker)[0][
                            'position'
                          ] >
                          getSortedTrackings(report.ranktracker)[1]['position']
                        "
                        :size="18"
                        :stroke-width="2" />
                      <ArrowUp
                        v-if="
                          getSortedTrackings(report.ranktracker)[0][
                            'position'
                          ] <
                            getSortedTrackings(report.ranktracker)[1][
                              'position'
                            ] ||
                          (getSortedTrackings(report.ranktracker)[0][
                            'position'
                          ] !==
                            getSortedTrackings(report.ranktracker)[1][
                              'position'
                            ] &&
                            getSortedTrackings(report.ranktracker)[0][
                              'position'
                            ] *
                              getSortedTrackings(report.ranktracker)[1][
                                'position'
                              ] <
                              0)
                        "
                        :size="18"
                        :stroke-width="2"
                    /></span>
                    <span
                      v-if="
                        report.ranktracker.length > 1 &&
                        (getSortedTrackings(report.ranktracker)[0][
                          'position'
                        ] === -1 ||
                          getSortedTrackings(report.ranktracker)[1][
                            'position'
                          ] === -1) &&
                        getSortedTrackings(report.ranktracker)[0][
                          'position'
                        ] !==
                          getSortedTrackings(report.ranktracker)[1]['position']
                      "
                      :class="
                        (getSortedTrackings(report.ranktracker)[0]['position'] >
                          -1 &&
                          getSortedTrackings(report.ranktracker)[1][
                            'position'
                          ] > -1 &&
                          getSortedTrackings(report.ranktracker)[0][
                            'position'
                          ] >
                            getSortedTrackings(report.ranktracker)[1][
                              'position'
                            ]) ||
                        (getSortedTrackings(report.ranktracker)[0][
                          'position'
                        ] === -1 &&
                          getSortedTrackings(report.ranktracker)[1][
                            'position'
                          ] > -1)
                          ? 'text-danger-100'
                          : getSortedTrackings(report.ranktracker)[0][
                              'position'
                            ] <
                              getSortedTrackings(report.ranktracker)[1][
                                'position'
                              ] ||
                            (getSortedTrackings(report.ranktracker)[0][
                              'position'
                            ] !==
                              getSortedTrackings(report.ranktracker)[1][
                                'position'
                              ] &&
                              getSortedTrackings(report.ranktracker)[0][
                                'position'
                              ] *
                                getSortedTrackings(report.ranktracker)[1][
                                  'position'
                                ] <
                                0)
                          ? 'text-success-100'
                          : ''
                      "
                    >
                      {{
                        (getSortedTrackings(report.ranktracker)[0]["position"] >
                          -1 &&
                          getSortedTrackings(report.ranktracker)[1][
                            "position"
                          ] > -1 &&
                          getSortedTrackings(report.ranktracker)[0][
                            "position"
                          ] >
                            getSortedTrackings(report.ranktracker)[1][
                              "position"
                            ]) ||
                        (getSortedTrackings(report.ranktracker)[0][
                          "position"
                        ] === -1 &&
                          getSortedTrackings(report.ranktracker)[1][
                            "position"
                          ] > -1)
                          ? "Lost"
                          : getSortedTrackings(report.ranktracker)[0][
                              "position"
                            ] <
                              getSortedTrackings(report.ranktracker)[1][
                                "position"
                              ] ||
                            (getSortedTrackings(report.ranktracker)[0][
                              "position"
                            ] !==
                              getSortedTrackings(report.ranktracker)[1][
                                "position"
                              ] &&
                              getSortedTrackings(report.ranktracker)[0][
                                "position"
                              ] *
                                getSortedTrackings(report.ranktracker)[1][
                                  "position"
                                ] <
                                0)
                          ? "New"
                          : ""
                      }}</span
                    >
                    <span>{{
                      report.ranktracker.length > 1
                        ? `${
                            getSortedTrackings(report.ranktracker)[1][
                              "position"
                            ] < 0
                              ? "100+"
                              : getSortedTrackings(report.ranktracker)[1][
                                  "position"
                                ]
                          }`
                        : ""
                    }}</span>
                    <ChartLine
                      :size="16"
                      :stroke-width="2"
                      @click="handlePositionGraph(report.ranktracker)"
                      class="cursor-pointer"
                    />
                  </div>
                </template>
                <template v-else>-</template>
              </td>
              <td>
                <div
                  v-if="
                    report.content_status && report.content_status?.ai_content
                  "
                >
                  <RouterLink
                    v-if="
                      report.content_status &&
                      report.content_status?.ai_content === 'Completed'
                    "
                    :to="{
                      name: 'editor',
                      params: { uuid: report.uuid },
                    }"
                    class="text-success-100"
                  >
                    <ClipboardCheck :size="20" :stroke-width="1.75"
                  /></RouterLink>
                  <span
                    v-else
                    class="inline-flex px-2 text-[11px] rounded text-white-100 py-1 bg-info-100"
                    >{{ report.content_status?.ai_content || "-" }}</span
                  >
                </div>
                <div v-else>
                  <ClipboardPlus
                    :size="20"
                    :stroke-width="1.75"
                    v-if="report.test_progress.percentage === 100"
                    class="cursor-pointer outline-none"
                    @click="handleShowGenerateArticleModal(report.uuid)"
                  />
                  <ClipboardPlus
                    :size="20"
                    :stroke-width="1.75"
                    class="outline-none"
                    v-else
                    v-tooltip="{
                      content: `<div style='max-width: 350px'>Article generation available once keyword testing is complete. Thanks for your patience!</div>`,
                      html: true,
                    }"
                  />
                </div>
              </td>
              <td>
                <span
                  class="cursor-pointer flex items-center gap-1"
                  :class="
                    report.content_spam_score.spam_score_percentage > 40
                      ? 'text-danger-100'
                      : 'text-success-100'
                  "
                  v-if="
                    report.content_spam_score &&
                    report.content_spam_score.spam_score_percentage
                  "
                  @click="showSpamScoreModal(report.content_spam_score, true)"
                >
                  {{
                    report.content_spam_score.spam_score_percentage
                      .toFixed(2)
                      .replace(".00", "")
                  }}</span
                >
                <span
                  class="cursor-pointer flex items-center gap-1"
                  :class="
                    getSpamScore(report.spam_score).spam_score_percentage > 40
                      ? 'text-danger-100'
                      : 'text-success-100'
                  "
                  @click="showSpamScoreModal(report.spam_score, false)"
                  v-else-if="
                    getSpamScore(report.spam_score) &&
                    getSpamScore(report.spam_score).spam_score_percentage
                  "
                >
                  {{
                    getSpamScore(report.spam_score)
                      .spam_score_percentage.toFixed(2)
                      .replace(".00", "")
                  }}
                </span>
                <span v-else>-</span>
              </td>
              <td>
                <span>
                  <RouterLink
                    v-if="
                      report.realtime_nlp_score &&
                      report.realtime_nlp_score?.kwdspy_percent
                    "
                    :to="{
                      name: 'editor',
                      params: { uuid: report.uuid },
                    }"
                  >
                    <span
                      :class="[
                        'flex items-center gap-1',
                        report.realtime_nlp_score?.kwdspy_percent <
                          report?.t10_avgs?.avg_kwdspy_percent - 5 ||
                        report.realtime_nlp_score?.kwdspy_percent >
                          report?.t10_avgs?.avg_kwdspy_percent + 5
                          ? 'text-danger-100'
                          : 'text-success-100',
                      ]"
                    >
                      {{ report.realtime_nlp_score?.kwdspy_percent }}
                    </span>
                  </RouterLink>
                  <span v-else>-</span>
                </span>
              </td>
              <td>
                <span>
                  <RouterLink
                    v-if="
                      getHeatScore(report) && getHeatScore(report)?.total_score
                    "
                    :to="{
                      name: 'editor',
                      params: { uuid: report.uuid },
                    }"
                  >
                    <span
                      :class="[
                        'flex items-center gap-1',
                        getHeatScore(report) &&
                        getHeatScore(report).questions &&
                        Object.values(getHeatScore(report).questions).some(
                          (number) => number <= 6
                        )
                          ? 'text-danger-100'
                          : 'text-success-100',
                      ]"
                    >
                      {{ getHeatScore(report).total_score }}
                    </span>
                  </RouterLink>
                  <span v-else>-</span>
                </span>
              </td>
              <td>
                <span
                  v-if="
                    report.gltr_details && report.gltr_details.avg_topk_lengths
                  "
                  :class="[
                    'flex items-center gap-1',
                    report.gltr_details &&
                    report.gltr_details.avg_topk_lengths > 4.1
                      ? ' text-danger-100'
                      : 'text-success-100',
                  ]"
                >
                  {{ report.gltr_details.avg_topk_lengths.toFixed(2) }}
                </span>
                <span v-else>-</span>
              </td>
              <td>
                <span
                  v-if="
                    report.difficulty_details &&
                    report.difficulty_details.avg_difficulty_diff_text
                  "
                  class="inline-flex items-center text-center h-7 px-2 text-[13px] font-medium rounded-md capitalize"
                  :class="
                    report?.difficulty_details?.avg_difficulty_diff_text
                      .toLowerCase()
                      .split(' ')
                      .join('-')
                  "
                  v-tooltip="
                    report.difficulty_details?.avg_difficulty_diff >= 0
                      ? `+${report.difficulty_details?.avg_difficulty_diff}`
                      : `${report.difficulty_details?.avg_difficulty_diff}`
                  "
                >
                  {{ report.difficulty_details?.avg_difficulty_diff_text }}
                </span>
                <span v-else>-</span>
              </td>
              <td>
                <span
                  v-if="
                    report.difficulty_details &&
                    report.difficulty_details.keyword_revenue
                  "
                >
                  ${{
                    Number(
                      report.difficulty_details.keyword_revenue.toFixed(0)
                    ).toLocaleString()
                  }}
                </span>
                <span v-else>-</span>
              </td>
              <td>
                <div
                  class="flex items-center relative min-w-[70px] max-w-[140px]"
                >
                  <select
                    class="form-field h-8 py-0 appearance-none text-sm px-2"
                    @change="changeProject($event.target.value, report.uuid)"
                  >
                    <option value="no-project" selected>No project</option>
                    <option
                      v-for="(project, key) in projects"
                      :key="key"
                      :value="project.uuid"
                      :selected="report.project?.uuid === project.uuid"
                    >
                      {{ project.name }}
                    </option>
                  </select>
                  <span
                    class="absolute pointer-events-none top-[1px] right-[2px] bottom-[1px] flex items-center justify-center w-5 z-10 rounded-tr-md rounded-br-md"
                    :class="mode === 'dark' ? 'bg-dark-70' : 'bg-white-100'"
                    ><ChevronDown :size="16" :stroke-width="1.75"
                  /></span>
                </div>
              </td>
              <td>
                <div
                  class="flex items-center gap-2"
                  v-if="
                    !isPossiblyIncompleted(report) &&
                    !isPossiblyIncompletedSecondStep(report)
                  "
                >
                  <div class="flex items-center gap-2">
                    <div
                      v-if="
                        isReportProcessing(report) && !isReportFailed(report)
                      "
                      class="spinner"
                    ></div>
                    <div class="w-14 mt-[2px]" v-if="!isReportFailed(report)">
                      <ProgressBar
                        class="bg-primary-100"
                        :max="maximumStatus"
                        :val="report?.test_progress?.percentage"
                        :mode="mode"
                      />
                    </div>
                    <label v-if="!isReportFailed(report)">
                      {{ Math.round(report?.test_progress?.percentage) }}%
                    </label>
                  </div>
                  <span
                    v-if="isReportFailed(report)"
                    @click="handleModalRerun(true, report)"
                    class="inline-flex items-center text-center h-8 px-2 text-[13px] font-medium rounded-md capitalize text-white-100 bg-danger-100 cursor-pointer w-full justify-center"
                    v-tooltip="{
                      content: `😒 Oops! Seems like one of our AIs failed to get your data. Click here to re-run this report.`,
                      placement: 'left',
                    }"
                    >Failed</span
                  >
                  <span
                    v-if="!isReportFailed(report) && isReportRetrying(report)"
                    class="inline-flex items-center text-center h-8 px-2 text-[13px] font-medium rounded-md capitalize text-black-100 bg-warning-100 w-full justify-center"
                    v-tooltip="{
                      content: `Our AI has detected that one of our TEST systems might have failed, we are automatically retrying to generate this report for you.`,
                      placement: 'left',
                    }"
                    >Retrying</span
                  >
                </div>
                <span
                  v-if="
                    isPossiblyIncompleted(report) &&
                    report?.test_progress?.percentage < 100
                  "
                  @click="handleModalRerun(true, report)"
                  class="inline-flex items-center text-center h-8 px-2 text-[13px] font-medium rounded-md capitalize text-white-100 bg-info-100 cursor-pointer w-full justify-center"
                  v-tooltip="{
                    content: `This report might be missing some tests possibly because of failure, or simply being an outdated test. If concerned, click here to re-run report.`,
                    placement: 'left',
                  }"
                  >Possibly Incomplete</span
                >
              </td>
              <td>
                <button
                  v-if="isWizard"
                  class="h-8 px-2 bg-primary-100 text-white-100 rounded-md"
                  @click="goToArticleBuilder(report)"
                >
                  Go to Article Builder
                </button>
                <DropdownMenu
                  v-else
                  :id="`column-actions-${report.uuid}`"
                  menuMinWidth="170"
                  :menuClass="[
                    mode === 'dark' ? '!bg-dark-80' : '!bg-primary-1 shadow-md',
                  ]"
                  :mode="mode"
                >
                  <template #toggle="{ toggleDropDown }">
                    <span @click="toggleDropDown" class="cursor-pointer">
                      <EllipsisVertical :size="22" :stroke-width="1.75" />
                    </span>
                  </template>
                  <div
                    class="divide-y bg-opacity-40"
                    :class="
                      mode === 'dark' ? 'divide-dark-50' : 'divide-gray-100'
                    "
                  >
                    <RouterLink
                      v-if="
                        !isReportFailed(report) && !isReportProcessing(report)
                      "
                      :to="{
                        name: 'serp-report-ta',
                        params: { uuid: report.uuid },
                        query: { tab: `syn` },
                      }"
                      class="flex p-2 transition-colors"
                      :class="
                        mode === 'dark'
                          ? 'text-white-100 hover:text-primary-100'
                          : 'text-black-100 hover:text-primary-100'
                      "
                      >Niche Competitors</RouterLink
                    >
                    <RouterLink
                      v-if="
                        !isReportFailed(report) && !isReportProcessing(report)
                      "
                      :to="{
                        name: 'serp-report-ta',
                        params: { uuid: report.uuid },
                        query: { tab: `results` },
                      }"
                      class="flex p-2 transition-colors"
                      :class="
                        mode === 'dark'
                          ? 'text-white-100 hover:text-primary-100'
                          : 'text-black-100 hover:text-primary-100'
                      "
                      >Keyword Results</RouterLink
                    >
                    <div
                      class="flex p-2 cursor-pointer transition-colors"
                      :class="
                        mode === 'dark'
                          ? 'text-white-100 hover:text-primary-100'
                          : 'text-black-100 hover:text-primary-100'
                      "
                      @click="handleModalRerun(true, report)"
                    >
                      Rerun
                    </div>
                    <div
                      class="flex p-2 cursor-pointer transition-colors"
                      :class="
                        mode === 'dark'
                          ? 'text-white-100 hover:text-primary-100'
                          : 'text-black-100 hover:text-primary-100'
                      "
                      @click="handleModalArchive(true, report)"
                    >
                      <template v-if="report.is_archive"> Restore </template>
                      <template v-else> Archive </template>
                    </div>
                    <div
                      class="flex p-2 cursor-pointer transition-colors"
                      :class="
                        mode === 'dark'
                          ? 'text-white-100 hover:text-primary-100'
                          : 'text-black-100 hover:text-primary-100'
                      "
                      @click="shareContentModal(report)"
                    >
                      Share
                    </div>
                  </div>
                </DropdownMenu>
              </td>
            </tr>
            <tr
              v-if="
                report.uuid === urlTrackReportId[index] &&
                isExpandTrackUrlDetails[index]
              "
            >
              <td colspan="13" class="!p-4">
                <div
                  class="w-full overflow-x-auto border rounded-lg max-h-[500px] overflow-y-auto without-scrollbar"
                  :class="
                    mode === 'dark'
                      ? 'bg-dark-70 border-dark-50'
                      : 'bg-white-100'
                  "
                >
                  <div
                    class="border-b p-4"
                    :class="mode === 'dark' ? 'border-dark-50' : ''"
                  >
                    <X
                      :size="22"
                      :stroke-width="1.75"
                      @click="isExpandTrackUrlDetails[index] = false"
                      class="cursor-pointer"
                    />
                  </div>
                  <table class="form-table w-full">
                    <thead
                      class="border-b-2 sticky top-0 z-10"
                      :class="mode === 'dark' ? 'border-dark-50' : ''"
                    >
                      <tr
                        :class="mode === 'dark' ? '!bg-dark-1' : '!bg-gray-1'"
                      >
                        <th>Keyword</th>
                        <th>Position</th>
                        <th>Volume</th>
                        <th>CPC Low</th>
                        <th>CPC High</th>
                        <th>Updated</th>
                      </tr>
                    </thead>
                    <tbody
                      class="divide-y bg-opacity-40"
                      :class="
                        mode === 'dark'
                          ? 'bg-dark-70 divide-dark-50'
                          : 'bg-white-100 divide-gray-100'
                      "
                    >
                      <template
                        v-if="
                          reportRanksDetailsItems[index] &&
                          reportRanksDetailsItems[index].length
                        "
                      >
                        <tr
                          v-for="item in reportRanksDetailsItems[index]"
                          :key="index"
                        >
                          <td>
                            {{ item?.keyword_data?.keyword || "-" }}
                          </td>
                          <td>
                            {{
                              item?.ranked_serp_element?.serp_item
                                ?.rank_absolute || 0
                            }}
                          </td>
                          <td>
                            {{
                              item?.keyword_data?.keyword_info?.search_volume ||
                              0
                            }}
                          </td>
                          <td>
                            {{
                              Number(
                                item?.keyword_data?.keyword_info
                                  ?.low_top_of_page_bid
                              ).toFixed(2) || 0
                            }}
                          </td>
                          <td>
                            {{
                              Number(
                                item?.keyword_data?.keyword_info
                                  ?.high_top_of_page_bid
                              ).toFixed(2) || 0
                            }}
                          </td>
                          <td>
                            {{
                              fromNow(
                                item?.ranked_serp_element?.last_updated_time
                              ) || "-"
                            }}
                          </td>
                        </tr>
                      </template>
                      <tr v-else>
                        <td colspan="13">
                          No additional keywords from this tracked page are
                          ranked in the top 100
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
          </template>
          <tr
            v-if="!userStore.isloading && !filteredReports.length"
            class="!bg-transparent"
          >
            <td colspan="13">
              <EmptyState :mode="mode" :notFound="'No reports found!'" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div
      class="border-t-4 rounded-bl-xl rounded-br-xl"
      :class="mode === 'dark' ? 'bg-dark-70 border-dark-50' : 'bg-white-100'"
    >
      <div class="flex items-center px-4 py-3 justify-between">
        <nav class="flex items-center gap-2">
          <button
            :disabled="!previousPageUrl"
            class="button primary-button"
            @click="loadReports('previous')"
          >
            <ArrowLeft
              :size="20"
              :stroke-width="1.75"
              class="mt-[2px]"
            />Previous
          </button>
          <button
            :disabled="!nextPageUrl || totalReport <= filteredReports.length"
            class="button primary-button"
            @click="loadReports('next')"
          >
            Next <ArrowRight :size="20" :stroke-width="1.75" class="mt-[2px]" />
          </button>
        </nav>
        <div class="w-40" v-if="!userStore.isloading && !isWizard">
          <Multiselect
            @change="handleChangeReportPerPage"
            :value="selectedReportsPerPage"
            :options="reportsPerPage"
            placeholder="Reports per page"
            :canClear="false"
            :searchable="false"
            :canDeselect="false"
            class="form-field to-top"
          ></Multiselect>
        </div>
      </div>
    </div>
    <div
      class="bg-opacity-40 fixed inset-0 z-[9999] flex items-center justify-center p-4"
      :class="mode === 'dark' ? 'bg-white-100' : 'bg-black-100'"
      v-if="showTrackingUrlModal"
    >
      <div
        class="max-w-2xl rounded-xl w-full mx-auto"
        :class="mode === 'dark' ? 'bg-dark-70' : 'bg-white-100'"
      >
        <div
          class="flex items-center justify-between p-4 border-b"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <h5 class="font-medium text-[17px] leading-none">
            Track position rankings
          </h5>
          <X
            :size="22"
            :stroke-width="1.75"
            @click="handleTrackingModalClose"
            class="cursor-pointer"
          />
        </div>
        <form @submit.prevent="addTrackingURL">
          <div
            class="without-scrollbar max-h-modal overflow-y-auto p-4"
            :class="mode === 'dark' ? 'bg-dark-70' : 'bg-gray-20 bg-opacity-30'"
          >
            <div>
              <label class="text-[15px] block mb-1">URL</label>
              <input
                class="form-field"
                type="text"
                v-model="trackRankingURL"
                placeholder="Enter URL"
                required
              />
            </div>
          </div>
          <div
            class="flex items-center justify-end p-4 border-t space-x-2"
            :class="mode === 'dark' ? 'border-dark-50' : ''"
          >
            <button type="submit" class="button primary-button">
              <Save :size="20" :stroke-width="1.75" />
              Continue
            </button>
          </div>
        </form>
      </div>
    </div>
    <div
      v-if="showRerunModal"
      class="bg-opacity-40 fixed inset-0 z-[9999] flex items-center justify-center p-4"
      :class="mode === 'dark' ? 'bg-white-100' : 'bg-black-100'"
    >
      <div
        class="max-w-xs rounded-xl w-full mx-auto p-4"
        :class="mode === 'dark' ? 'bg-dark-70' : 'bg-white-100'"
      >
        <h4 class="text-center text-lg font-medium">Are you sure?</h4>
        <div class="flex items-center justify-center pt-4 space-x-2">
          <button
            class="button close-button small-button"
            @click="handleModalRerun(false, null)"
          >
            No
          </button>
          <button
            class="button primary-button small-button"
            @click="handleRerunReport"
          >
            Yes
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="showArchiveModal"
      class="bg-opacity-40 fixed inset-0 z-[9999] flex items-center justify-center p-4"
      :class="mode === 'dark' ? 'bg-white-100' : 'bg-black-100'"
    >
      <div
        class="max-w-xs rounded-xl w-full mx-auto p-4"
        :class="mode === 'dark' ? 'bg-dark-70' : 'bg-white-100'"
      >
        <h4 class="text-center text-lg font-medium">Are you sure?</h4>
        <div class="flex items-center justify-center pt-4 space-x-2">
          <button
            class="button close-button small-button"
            @click="handleModalArchive(false, null)"
          >
            No
          </button>
          <button
            class="button primary-button small-button"
            @click="handleArchiveReport"
          >
            Yes
          </button>
        </div>
      </div>
    </div>
    <RankTrackerGraph
      v-if="activeRankTracker"
      :positions="activeRankTracker"
      @close="handlePositionGraph(null)"
      :mode="mode"
    />
    <SpamScoreModal
      v-if="showSpamScore"
      :show="showSpamScore"
      :spamScore="currentSpamScore"
      :spamScorePer="spamScorePer"
      @close="closeSpamScoreModal"
      :mode="mode"
    />
    <AutoGeneratedArticleInfo
      v-if="
        isWizard &&
        userStore.isShowAutoGeneratedArticleInfoModal &&
        !userStore.isloading
      "
      :mode="mode"
    />
    <div
      class="bg-opacity-40 fixed inset-0 z-[9999] flex items-center justify-center p-4"
      :class="mode === 'dark' ? 'bg-white-100' : 'bg-black-100'"
      v-if="isAutoGenerateAiArticleCompleted"
    >
      <div
        class="max-w-xl rounded-xl w-full mx-auto"
        :class="mode === 'dark' ? 'bg-dark-70' : 'bg-white-100'"
      >
        <div
          class="flex items-center justify-between p-4 border-b"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <h5 class="font-medium text-[17px] leading-none">
            Auto AI article not generated yet
          </h5>
          <X
            :size="22"
            :stroke-width="1.75"
            @click="isAutoGenerateAiArticleCompleted = false"
            class="cursor-pointer"
          />
        </div>
        <div
          class="without-scrollbar max-h-modal overflow-y-auto p-4 rounded-bl-xl rounded-br-xl"
          :class="mode === 'dark' ? 'bg-dark-70' : 'bg-gray-20 bg-opacity-30'"
        >
          <div class="p-4 text-center">Auto AI article not generated yet.</div>
        </div>
      </div>
    </div>
    <div
      class="bg-opacity-40 fixed inset-0 z-[9999] flex items-center justify-center p-4"
      :class="mode === 'dark' ? 'bg-white-100' : 'bg-black-100'"
      v-if="showGenerateArticleModal"
    >
      <div
        class="max-w-3xl rounded-xl w-full mx-auto"
        :class="mode === 'dark' ? 'bg-dark-70' : 'bg-white-100'"
      >
        <div
          class="flex items-center justify-between p-4 border-b"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <h5 class="font-medium text-[17px] leading-none">
            High quality article (HQA) V2.1 (Beta)
          </h5>
          <X
            :size="22"
            :stroke-width="1.75"
            @click="hideGenerateArticleModal"
            class="cursor-pointer"
          />
        </div>
        <div
          class="without-scrollbar max-h-modal overflow-y-auto p-4"
          :class="mode === 'dark' ? 'bg-dark-70' : 'bg-gray-20 bg-opacity-30'"
        >
          <div class="grid grid-cols-3 gap-4">
            <div class="col-span-3">
              <label for="topic" class="text-[15px] mb-1 block"
                >Topic of article: (AI assisted)</label
              >
              <input
                type="text"
                v-model="topicOfArticle"
                placeholder="Topic"
                class="form-field"
                id="topic"
              />
            </div>
            <div class="col-span-3">
              <label for="match-ai" class="text-[15px] mb-1 block"
                >Match AI analysis competitor page type: (Recommended)</label
              >
              <input
                type="text"
                v-model="instructionsOfArticle"
                placeholder="Instructions"
                class="form-field"
                id="match-ai"
              />
            </div>
            <div class="col-span-3 flex items-center gap-4">
              <div
                class="h-11 border rounded-md flex items-center justify-between px-4 flex-1"
                :class="[
                  mode === 'dark'
                    ? 'bg-dark-70 border-dark-50'
                    : 'border-gray-30 border-opacity-50 bg-white-100',
                ]"
              >
                <div class="checkbox">
                  <input
                    type="checkbox"
                    value="url"
                    id="useCustomKnowledgeBase"
                    v-model="useCustomKnowledgeBase"
                  />
                  <label for="useCustomKnowledgeBase"
                    >Use custom knowledge base</label
                  >
                </div>
                <a
                  href="https://www.seocopilotai.com/custom-knowledge-base"
                  target="_blank"
                  class="w-5 h-5 -mr-1 hover:text-primary-100"
                  v-tooltip="{
                    content: `Custom knowledge base guidelines`,
                    placement: 'left',
                  }"
                  ><CircleHelp :size="20" :stroke-width="1.75"
                /></a>
              </div>
              <div
                v-if="useCustomKnowledgeBase"
                class="h-11 border rounded-md flex items-center px-4 relative flex-1"
                :class="[
                  mode === 'dark'
                    ? 'bg-dark-70 border-dark-50'
                    : 'border-gray-30 border-opacity-50 bg-white-100',
                ]"
              >
                <label
                  for="useCustomKnowledgeBaseFile"
                  class="absolute inset-0 flex items-center justify-center text-[15px] cursor-pointer gap-1.5"
                >
                  <input
                    type="file"
                    class="hidden"
                    id="useCustomKnowledgeBaseFile"
                    accept=".txt"
                    @change="uploadCustomKnowledgeBaseFile"
                  />
                  <span v-if="useCustomKnowledgeBaseFile === ''"
                    >Import file</span
                  >
                  <span v-else class="text-wrap max-w-[100px]">{{
                    useCustomKnowledgeBaseFile.name
                  }}</span>
                  <span
                    v-if="useCustomKnowledgeBaseFile !== ''"
                    @click.prevent="deleteCustomKnowledgeBaseFile"
                    class="cursor-pointer hover:text-danger-100"
                  >
                    <Trash2 :size="20" :stroke-width="1.75" />
                  </span>
                </label>
              </div>
            </div>
            <div
              class="h-11 border rounded-md flex items-center px-4"
              :class="
                mode === 'dark'
                  ? 'bg-dark-70 border-dark-50'
                  : 'border-gray-30 border-opacity-50 bg-white-100'
              "
            >
              <div class="checkbox">
                <input
                  type="checkbox"
                  value="url"
                  id="genURL"
                  v-model="articleSelectedPrompts"
                />
                <label for="genURL">URL</label>
              </div>
            </div>
            <div
              class="h-11 border rounded-md flex items-center px-4"
              :class="
                mode === 'dark'
                  ? 'bg-dark-70 border-dark-50'
                  : 'border-gray-30 border-opacity-50 bg-white-100'
              "
            >
              <div class="checkbox">
                <input
                  type="checkbox"
                  value="title"
                  id="genTitle"
                  v-model="articleSelectedPrompts"
                />
                <label for="genTitle">Title</label>
              </div>
            </div>
            <div
              class="h-11 border rounded-md flex items-center px-4"
              :class="
                mode === 'dark'
                  ? 'bg-dark-70 border-dark-50'
                  : 'border-gray-30 border-opacity-50 bg-white-100'
              "
            >
              <div class="checkbox">
                <input
                  type="checkbox"
                  value="md"
                  id="genMD"
                  v-model="articleSelectedPrompts"
                />
                <label for="genMD">Meta description</label>
              </div>
            </div>
            <div class="col-span-3">
              <div
                class="rounded-lg px-4 py-2 flex gap-2"
                :class="mode === 'dark' ? 'bg-warning-100' : 'bg-warning-50'"
              >
                <div class="flex-1">
                  <span class="text-[15px] text-black-100"
                    >Please be aware that by proceeding with the upcoming
                    action, your current content in the Article Builder will be
                    permanently replaced.</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          class="flex items-center justify-end p-4 border-t space-x-2"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <button class="button primary-button" @click="generateArticle">
            <StepForward :size="20" :stroke-width="1.75" />
            Continue
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="showShareModal"
      class="bg-opacity-40 fixed inset-0 z-[9999] flex items-center justify-center p-4"
      :class="mode === 'dark' ? 'bg-white-100' : 'bg-black-100'"
    >
      <div
        class="max-w-2xl rounded-xl w-full mx-auto"
        :class="mode === 'dark' ? 'bg-dark-70' : 'bg-white-100'"
      >
        <div
          class="flex items-center justify-between p-4 border-b"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <h5 class="font-medium text-[17px] leading-none">
            Share content editor
          </h5>
          <X
            :size="22"
            :stroke-width="1.75"
            @click="showShareModal = false"
            class="cursor-pointer"
          />
        </div>
        <div
          class="without-scrollbar max-h-modal overflow-y-auto p-4"
          :class="mode === 'dark' ? 'bg-dark-70' : 'bg-gray-20 bg-opacity-30'"
        >
          <p class="text-[15px] mb-1">
            Anyone with this link can view this page.
          </p>
          <div
            class="p-3 rounded-lg bg-opacity-40"
            :class="mode === 'dark' ? 'bg-dark-50' : 'bg-gray-100'"
          >
            <code class="text-[15px]">
              {{ editorPublicLink }}
            </code>
          </div>
        </div>
        <div
          class="flex items-center justify-end p-4 border-t space-x-2"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <button class="button primary-button" @click="copyShareLink">
            <Clipboard :size="20" :stroke-width="1.75" />
            Copy link to clipboard
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import moment from "moment";
import ReportFilter from "@/components/ReportFilter.vue";
import helper from "../libs/helper";
import { useUserStore } from "../stores/user";
import ApiRequest from "../libs/ApiRequest";
import { findFlagUrlByIso2Code } from "country-flags-svg";
import FilterByProjectForm from "../components/FilterByProjectForm.vue";
import DataTableSortComponent from "../components/DataTableSortComponent.vue";
import DropdownMenu from "../components/common/DropdownMenu.vue";
import EmptyState from "../components/common/Empty.vue";
import RankTrackerGraph from "../components/RankTrackerGraph.vue";
import Multiselect from "@vueform/multiselect";
import ProgressBar from "../components/common/ProgressBar.vue";
import SpamScoreModal from "../components/kd/SpamScoreModal.vue";
import { changeArrayObjectOrder } from "../libs";
import AutoGeneratedArticleInfo from "../components/AutoGeneratedArticleInfo.vue";
import {
  ArrowLeft,
  ArrowRight,
  ChartLine,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  Clipboard,
  ClipboardCheck,
  ClipboardPlus,
  EllipsisVertical,
  ExternalLink,
  FileChartColumnIncreasing,
  Link,
  Package,
  Pencil,
  Save,
  StepForward,
  Trash2,
  X,
  CircleHelp,
  ArrowDown,
  ArrowUp,
} from "lucide-vue-next";
export default {
  setup() {
    const userStore = useUserStore();
    return {
      userStore,
      selectedProject: computed(() => userStore.selectedProject),
      enteredReportSearchQuery: computed(
        () => userStore.enteredReportSearchQuery
      ),
      projects: computed(() => userStore.projects),
      phases: computed(() => userStore.phases),
    };
  },
  props: {
    mode: String,
    pageSize: {
      type: Number,
      default: 20,
    },
    isHideTopBar: {
      type: Boolean,
      default: false,
    },
    isWizard: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showSpamScore: false,
      currentSpamScore: null,
      spamScorePer: 0,
      isShowOutdatedReports: false,
      urlTrackReportId: {},
      isExpandTrackUrlDetails: {},
      reportRanksDetailsItems: {},
      selectedReportsPerPage: 20,
      reportsPerPage: [
        { label: 20, value: 20 },
        { label: 50, value: 50 },
        { label: 100, value: 100 },
        { label: 250, value: 250 },
        { label: 500, value: 500 },
      ],
      currentPageUrl: null,
      reports: [],
      totalReport: 0,
      currentPage: 1,
      hasNextPage: false,
      nextPageUrl: null,
      previousPageUrl: null,
      showIsArchived: false,
      showTrackingUrlModal: false,
      trackRankingURL: null,
      addRankingUrlFor: null,
      selectedStatus: null,
      showRerunModal: false,
      showArchiveModal: false,
      activeReport: null,
      activeRankTracker: null,
      maximumStatus: 100,
      refreshPhaseStatusInterval: null,
      pendingOrProcessingReports: [],
      reportPhaseUpdating: false,
      isAutoGenerateAiArticleCompleted: false,
      reportGeneratingArticleUUID: "",
      showGenerateArticleModal: false,
      articleSelectedPrompts: [],
      showShareModal: false,
      editorPublicLink: "",
      topicOfArticle: null,
      instructionsOfArticle:
        "It is recommended to keep this text, OR give page style/type instructions here",
      useCustomKnowledgeBase: false,
      useCustomKnowledgeBaseFile: "",
      psqiQueries: null,
    };
  },
  computed: {
    getTooltipLabel() {
      return this.isShowOutdatedReports
        ? "Show All Report"
        : "Filter to display reports that are over 90 days old. We recommend you re-run these reports to get the most recent updates and re-optimize your content.";
    },
    filteredReports() {
      let reports = this.reports;
      if (this.selectedStatus) {
        reports = reports.filter((r) => {
          return this.selectedStatus in r.content_status;
        });
      }
      return reports;
    },
  },
  components: {
    CircleHelp,
    Trash2,
    ArrowRight,
    ArrowLeft,
    EllipsisVertical,
    StepForward,
    ChartLine,
    ClipboardCheck,
    ClipboardPlus,
    Pencil,
    ChevronDown,
    ChevronRight,
    Save,
    X,
    Link,
    ExternalLink,
    Clipboard,
    FileChartColumnIncreasing,
    CircleCheck,
    Package,
    ReportFilter,
    FilterByProjectForm,
    DataTableSortComponent,
    ProgressBar,
    DropdownMenu,
    RankTrackerGraph,
    Multiselect,
    EmptyState,
    SpamScoreModal,
    AutoGeneratedArticleInfo,
    ArrowDown,
    ArrowUp,
  },
  async mounted() {
    this.selectedReportsPerPage = this.pageSize;
    this.userStore.setSelectedProject(this.$route.query.project || "");
    this.userStore.setReportSearchQuery("");
    this.loadReports();
    await this.refreshPhaseStatus();
  },
  unmounted() {
    this.refreshPhaseStatusInterval &&
      clearInterval(this.refreshPhaseStatusInterval);
  },
  methods: {
    uploadCustomKnowledgeBaseFile(e) {
      const files = e.target.files || e.dataTransfer.files;
      if (!files.length) return;
      if (files.length === 1) {
        this.useCustomKnowledgeBaseFile = files[0];
      } else {
        this.$toast.error("You can only upload 1 file at a time");
      }
    },
    deleteCustomKnowledgeBaseFile() {
      this.useCustomKnowledgeBaseFile = "";
      this.useCustomKnowledgeBase = false;
    },
    hideGenerateArticleModal() {
      this.showGenerateArticleModal = false;
      this.useCustomKnowledgeBaseFile = "";
      this.useCustomKnowledgeBase = false;
      this.articleSelectedPrompts = [];
    },
    async generateArticle() {
      try {
        this.userStore.setIsLoading(!0);
        const payload = {
          search_uuid: this.reportGeneratingArticleUUID,
          generate_meta_description: this.articleSelectedPrompts.includes("md"),
          generate_title: this.articleSelectedPrompts.includes("title"),
          generate_article: true,
          generate_url: this.articleSelectedPrompts.includes("url"),
          topic_of_article: this.topicOfArticle,
          template_instructions: this.instructionsOfArticle,
          use_custom_knowledge_base: this.useCustomKnowledgeBase,
          custom_knowledge_base_file: this.useCustomKnowledgeBaseFile,
        };
        if (
          this.instructionsOfArticle ===
            "It is recommended to keep this text, OR give page style/type instructions here" ||
          this.instructionsOfArticle === ""
        ) {
          delete payload.template_instructions;
        }
        if (this.useCustomKnowledgeBaseFile === "") {
          delete payload.custom_knowledge_base_file;
        }
        const formData = new FormData();
        for (const key in payload) {
          formData.append(key, payload[key]);
        }
        const { data } = await ApiRequest().post(
          `api/v2/zebraai/editor-prompts`,
          formData
        );
        this.$toast.success(data.detail.message);
        this.reports.forEach((report) => {
          if (report.uuid === this.reportGeneratingArticleUUID) {
            report.content_status.ai_content = "Analyzing";
          }
        });
        this.showGenerateArticleModal = false;
        this.userStore.setIsLoading(!1);
      } catch (error) {
        this.showGenerateArticleModal = false;
        this.userStore.setIsLoading(!1);
        const extractedError = helper.extractAlert(error);
        this.$toast.error(extractedError.alert.message);
      }
    },
    handleShowGenerateArticleModal(uuid) {
      this.getPSQI(uuid);
      this.showGenerateArticleModal = true;
      this.reportGeneratingArticleUUID = uuid;
    },
    async getPSQI(uuid) {
      try {
        const URL = `/api/v1/features/searches/${uuid}/psqi`;
        const res = await ApiRequest().get(URL);
        if (res.status !== 200) {
          throw new Error("Something went wrong");
        }
        if (res.data) {
          this.psqiQueries = res.data;
          const psqiQueries = [];
          if (this.psqiQueries["primary_query_intent"]) {
            psqiQueries.push(this.psqiQueries["primary_query_intent"]);
          }
          if (this.psqiQueries["secondary_query_intent"]) {
            psqiQueries.push(this.psqiQueries["secondary_query_intent"]);
          }
          this.topicOfArticle = psqiQueries.join("; ");
        }
      } catch (error) {
        const extractedError = helper.extractAlert(error, "nm");
        if (extractedError.redirect) {
          this.userStore.signOut();
          this.$router.push({ name: extractedError.redirect });
        }
      }
    },
    getHeatScore(report) {
      if (report.heeat_score) {
        if (report.heeat_score["content"]) {
          return report.heeat_score["content"];
        } else if (report.heeat_score["-1"]) {
          return report.heeat_score["-1"];
        } else {
          return null;
        }
      }
      return "-";
    },
    closeSpamScoreModal() {
      this.showSpamScore = false;
      this.currentSpamScore = null;
    },
    showSpamScoreModal(spam_score, isContentSpamScore = false) {
      this.showSpamScore = true;
      const order = [];
      const spots = isContentSpamScore
        ? spam_score
        : this.getSpamScore(spam_score);
      this.spamScorePer = spots.spam_score_percentage || 0;
      if (spots && spots.details) {
        spots.details.forEach((spot) => {
          order.push(spot.spam_type);
        });
        this.currentSpamScore = changeArrayObjectOrder(
          spots.details,
          order,
          "spam_type"
        );
      }
    },
    getSpamScore(spam_score) {
      return spam_score
        ? spam_score.find((s) => s.serp_position === -1 || !s.serp_position)
        : null;
    },
    findCommonObjects(array1, array2) {
      return array1.filter((obj1) =>
        array2.some((obj2) => obj1.uuid === obj2.uuid)
      );
    },
    getReportsWithProcessingOrPending(reports) {
      if (this.isWizard) {
        const generatingArticlesReport = this.findCommonObjects(
          reports,
          this.userStore.autoGeneratingArticles
        );
        return generatingArticlesReport.filter((report) => {
          const details = report.test_progress.details;
          return (
            report.content_status.ai_content !== "Completed" ||
            Object.values(details).some(
              (value) =>
                value === "processing" ||
                value === "pending" ||
                value === "Processing" ||
                value === "Pending"
            )
          );
        });
      }
      return reports.filter((report) => {
        const details = report.test_progress.details;
        return (
          ((report.content_status &&
            report.content_status.ai_content &&
            report.content_status.ai_content !== "Completed" &&
            report.content_status.ai_content !== "Fail due to AI") ||
            Object.values(details).some(
              (value) =>
                value === "processing" ||
                value === "pending" ||
                value === "Processing" ||
                value === "Pending"
            )) &&
          !this.isReportFailed(report) &&
          !this.isPossiblyIncompletedSecondStep(report)
        );
      });
    },
    async refreshPhaseStatus() {
      this.refreshPhaseStatusInterval = setInterval(async () => {
        this.pendingOrProcessingReports =
          this.getReportsWithProcessingOrPending(this.filteredReports);
        if (this.pendingOrProcessingReports.length > 0) {
          const uuids = this.pendingOrProcessingReports.map((ele) => ele.uuid);
          try {
            let reportPhases = [];
            this.reportPhaseUpdating = true;
            if (this.reportPhaseUpdating) {
              reportPhases = await this.getReportPhase(uuids);
            }
            this.reportPhaseUpdating = false;
            for (const item of reportPhases) {
              const matchingReport = this.filteredReports.find(
                (x) => x.uuid === item.uuid
              );
              if (matchingReport) {
                matchingReport.test_progress = item.test_progress;
                matchingReport.content_status.ai_content =
                  item.ai_content_status;
              }
            }
          } catch (error) {
            console.error("Error in getReportPhase function:", error);
          }
        }
      }, 5000);
    },
    async getReportPhase(arrUuids) {
      try {
        const postData = { uuids: arrUuids };
        const { data } = await ApiRequest().post(
          "/api/serp/search/report_phase",
          postData
        );
        return data.data;
      } catch (error) {
        console.error("Error in getReportPhase function:", error);
        throw error;
      }
    },
    handlePositionGraph(positions) {
      let newPositions = [];
      newPositions =
        positions &&
        positions.reduce((acc, curr) => {
          if (!acc.some((p) => p.tracker_date === curr.tracker_date)) {
            acc.push(curr);
          }
          return acc;
        }, []);
      this.activeRankTracker = newPositions;
    },
    handleModalRerun(type, report) {
      this.showRerunModal = type;
      this.activeReport = report || null;
    },
    handleRerunReport() {
      this.reRunReport(this.activeReport);
      this.showRerunModal = false;
    },
    handleModalArchive(type, report) {
      this.showArchiveModal = type;
      this.activeReport = report || null;
    },
    handleArchiveReport() {
      this.archiveReport(this.activeReport);
      this.showArchiveModal = false;
    },
    shareContentModal(report) {
      this.editorPublicLink = `${window.location.origin}/article-builder/public/${report.uuid}`;
      this.showShareModal = true;
    },
    async copyToBoard(query) {
      const { alertType, message } = await helper.copyToClipboard(query);
      if (alertType === "error") {
        this.$toast.error(message);
      } else {
        this.$toast.success(message);
      }
    },
    async copyShareLink() {
      const { alertType, message } = await helper.copyToClipboard(
        this.editorPublicLink
      );
      this.showShareModal = false;
      if (alertType === "error") {
        this.$toast.error(message);
      } else {
        this.$toast.success("Share link is copied to clipboard");
      }
    },
    handleChangeSelection(value, callback) {
      this.selectedStatus = value === this.selectedStatus ? null : value;
      callback();
    },
    getSortedTrackings(tracks) {
      return tracks.sort(function (a, b) {
        return new Date(b.tracker_date) - new Date(a.tracker_date);
      });
    },
    handleTrackingModal(report) {
      this.addRankingUrlFor = report;
      this.trackRankingURL = report.track_url;
      this.showTrackingUrlModal = true;
    },
    goToArticleBuilder(report) {
      if (report.content_status.ai_content === "Completed") {
        this.$router.push({ name: "editor", params: { uuid: report.uuid } });
      } else {
        this.isAutoGenerateAiArticleCompleted = true;
      }
    },
    handleUrlOpen(url) {
      const parts = url.split("//");
      const newURL =
        parts[0].includes("http") || parts[0].includes("https")
          ? url
          : `http://${url}`;
      return newURL;
    },
    handleTrackingModalClose() {
      this.addRankingUrlFor = null;
      this.trackRankingURL = null;
      this.showTrackingUrlModal = false;
    },
    handleTrackURLChange(event, report) {
      if (event.target.value === "20") {
        this.updateTracking(report, 10);
      } else {
        this.updateTracking(report);
      }
    },
    async addTrackingURL() {
      if (!this.trackRankingURL) {
        return;
      }
      const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
      if (!urlPattern.test(this.trackRankingURL)) {
        this.$toast.error("Invalid URL");
        return;
      }
      try {
        this.userStore.setIsLoading(!0);
        const dateNow = moment();
        dateNow.add("1", "y");
        const { data } = await ApiRequest().put(
          `/api/serp/search/${this.addRankingUrlFor.uuid}`,
          {
            track_url_status: 20,
            track_url: this.trackRankingURL,
            track_up_till: dateNow.format("YYYY-MM-DD"),
          }
        );
        this.reports.map((p) => {
          if (p.uuid === this.addRankingUrlFor.uuid) {
            p.track_url_status = data.track_url_status;
            p.track_url = data.track_url;
            p.track_up_till = data.track_up_till;
          }
        });
        this.userStore.setIsLoading(!1);
        this.$toast.success(
          `successfully ${
            this.addRankingUrlFor.track_url ? "updated" : "added"
          } URL`
        );
        this.handleTrackingModalClose();
      } catch (error) {
        this.userStore.setIsLoading(!1);
        this.$toast.error(
          `Failed to ${this.addRankingUrlFor.track_url ? "update" : "add"} URL!`
        );
      }
    },
    async updateTracking(report, status = 20) {
      try {
        this.userStore.setIsLoading(!0);
        const dateNow = moment();
        dateNow.add("1", "y");
        const { data } = await ApiRequest().put(
          `/api/serp/search/${report.uuid}`,
          {
            track_url_status: status,
            track_url: report.track_url,
            track_up_till:
              status == 10
                ? report.track_up_till
                : dateNow.format("YYYY-MM-DD"),
          }
        );
        this.reports.map((p) => {
          if (p.uuid === report.uuid) {
            p.track_url_status = data.track_url_status;
            p.track_url = data.track_url;
            p.track_up_till = data.track_up_till;
          }
        });
        this.userStore.setIsLoading(!1);
        this.$toast.success(
          `Successfully ${status == 20 ? "enabled" : "disabled"} tracking!`
        );
      } catch (error) {
        this.userStore.setIsLoading(!1);
        this.$toast.error(
          `Failed to ${status == 20 ? "enable" : "disable"} tracking!`
        );
      }
    },
    flagURL(country) {
      const countryCode = country == "uk" || country == "UK" ? "gb" : country;
      if (!countryCode) return "";
      return findFlagUrlByIso2Code(countryCode);
    },
    fromNow(value) {
      return moment(value).format("MMMM-D");
    },
    getSearhEngine(report) {
      const html = `<div class="flex items-center gap-2">
        Search Engine:
          <img
            style='width: 20px; height: 20px; margin-right: 4px; margin-left: 8px'
              src='https://online.seranking.com/frontend-dist/ContentEditor/img/googlef7dfa4630f775144939a.svg'
          />
        <span style='margin-right: 4px'>
          ${report.google_domain ? report.google_domain : "google.com"}
          </span>
        <img
          style='width: 30px; height: 30px; margin-right: 4px'
          v-if='${report.location}'
            src='${this.flagURL(report.location)}'
        />
        <span class="uppercase">
          ${report.language ? report.language : "EN"}
        </span>
      </div>`;
      return html;
    },
    dataFormat(value) {
      return moment(value).format("MM/DD/YYYY hh:mm (Z)");
    },
    handleChangeReportPerPage(count) {
      this.selectedReportsPerPage = count;
      this.loadReports();
    },
    async loadRankDetails(reportID, index) {
      try {
        this.userStore.setIsLoading(true);
        const res = await ApiRequest().get(
          `api/serp/search/${reportID}/rank_details`
        );
        if (res.status === 200) {
          this.$toast.info(res.data.details);
        }
        const response = await ApiRequest().get(
          `api/serp/search/${reportID}/get_rank_details`
        );
        this.reportRanksDetailsItems[index] = response.data.result.items;
        this.urlTrackReportId[index] = reportID;
        this.isExpandTrackUrlDetails[index] = true;
      } catch (error) {
        this.userStore.setIsLoading(false);
        const extractedError = helper.extractAlert(error, "ranks");
        this.$toast.error(extractedError.alert.message);
      } finally {
        this.userStore.setIsLoading(false);
      }
    },
    loadReportsToggle() {
      this.isShowOutdatedReports = !this.isShowOutdatedReports;
      this.loadReports();
    },
    async loadReports(page) {
      const date = new Date();
      const targetDate = moment(date).subtract(90, "day").format("YYYY-MM-DD");
      try {
        this.userStore.setIsLoading(!0);
        let reportData;
        let queries = {
          is_archive: this.showIsArchived,
          limit: this.selectedReportsPerPage,
          ...(this.isShowOutdatedReports ? { created_at__lt: targetDate } : {}),
          ...(this.enteredReportSearchQuery
            ? { q: this.enteredReportSearchQuery }
            : {}),
        };
        if (this.selectedProject) {
          queries.project = this.selectedProject;
        }
        queries = new URLSearchParams(queries).toString();
        if (page === "current") {
          const { data } = await ApiRequest().get(this.currentPageUrl);
          reportData = data;
        } else if (page === "next" && this.nextPageUrl) {
          this.currentPageUrl = this.nextPageUrl;
          const { data } = await ApiRequest().get(this.nextPageUrl);
          reportData = data;
        } else if (page === "previous" && this.previousPageUrl) {
          this.currentPageUrl = this.previousPageUrl;
          const { data } = await ApiRequest().get(this.previousPageUrl);
          reportData = data;
        } else {
          const url = `/api/serp/search?${queries}`;
          const { data } = await ApiRequest().get(url);
          reportData = data;
          this.currentPageUrl = url;
        }
        const {
          results = [],
          next = null,
          count = 0,
          previous = null,
        } = reportData;
        this.reports = helper.lodashOrder(results, "created_at", "desc");
        this.totalReport = count;
        this.previousPageUrl = previous;
        this.nextPageUrl = next;
        this.hasNextPage = Boolean(next);
        this.userStore.setIsLoading(!1);
      } catch (error) {
        this.userStore.setIsLoading(!1);
        const extractedError = helper.extractAlert(error, "reports");
        this.$toast.error(extractedError.alert.message);
        if (extractedError.redirect) {
          this.userStore.signOut();
          this.$router.push({ name: extractedError.redirect });
        }
      }
    },
    async loadReportsByProject() {
      try {
        this.userStore.setIsLoading(!0);
        const res = await ApiRequest().get(
          `api/serp/projects/${this.selectedProject}`
        );
        if (res.status !== 200) {
          throw new Error("Something went wrong!");
        }
        const results = res.data.searches;
        this.reports = helper.lodashOrder(results, "created_at", "desc");
        this.userStore.setIsLoading(!1);
      } catch (error) {
        this.userStore.setIsLoading(!1);
        const extractedError = helper.extractAlert(error, "project-details");
        this.$toast.error(extractedError.alert.message);
        if (extractedError.redirect) {
          this.userStore.signOut();
          this.$router.push({ name: extractedError.redirect });
        }
      }
    },
    async loadReportsWithFilter(page) {
      const date = new Date();
      const targetDate = moment(date).subtract(90, "day").format("YYYY-MM-DD");
      try {
        page = page < 1 ? 1 : page;
        this.userStore.setIsLoading(!0);
        let queries = {
          page: page,
          is_archive: this.showIsArchived,
          q: this.enteredReportSearchQuery,
          ...(this.isShowOutdatedReports ? { created_at__lt: targetDate } : {}),
        };
        if (this.selectedProject) {
          queries.project = this.selectedProject;
        }
        queries = new URLSearchParams(queries).toString();
        const { data } = await ApiRequest().get(`api/serp/search?${queries}`);
        const { results = [], next = null, count = 0 } = data;
        this.totalReport = count;
        this.reports = helper.lodashOrder(results, "created_at", "desc");
        this.hasNextPage = Boolean(next);
        this.currentPage = page;
        this.userStore.setIsLoading(!1);
      } catch (error) {
        this.userStore.setIsLoading(!1);
        const extractedError = helper.extractAlert(error, "reports");
        this.$toast.error(extractedError.alert.message);
        if (extractedError.redirect) {
          this.userStore.signOut();
          this.$router.push({ name: extractedError.redirect });
        }
      }
    },
    async changeProject(projectUUID, searchUUID) {
      try {
        this.userStore.setIsLoading(!0);
        if (projectUUID === "no-project") {
          projectUUID = null;
        }
        const postData = { project: projectUUID };
        const { data } = await ApiRequest().post(
          `/api/serp/search/${searchUUID}/assign-to-project`,
          postData
        );
        if (data.uuid) {
          this.$toast.success("Project assigned successfully");
          this.reports = this.reports.map((report) => {
            if (report.uuid !== data.uuid) {
              return report;
            }
            const project = {
              project: data.project,
            };
            return {
              ...report,
              ...project,
            };
          });
        } else {
          throw new Error("Something went wrong");
        }
        this.userStore.setIsLoading(!1);
      } catch (error) {
        this.userStore.setIsLoading(!1);
        const extractedError = helper.extractAlert(error, "reports");
        this.$toast.error(extractedError.alert.message);
        if (extractedError.redirect) {
          this.userStore.signOut();
          this.$router.push({ name: extractedError.redirect });
        }
      }
    },
    shortData(by) {
      this.reports = helper.lodashOrder(this.reports, by);
    },
    loadReportOrProject() {
      if (this.enteredReportSearchQuery) {
        this.loadReportsWithFilter(1);
      } else {
        this.loadReports();
      }
    },
    async archiveReport(report) {
      try {
        this.userStore.setIsLoading(!0);
        const postData = { is_archive: !report.is_archive };
        const res = await ApiRequest().post(
          `/api/serp/search/${report.uuid}/archive`,
          postData
        );
        if (res.status === 200) {
          this.reports = this.reports.filter((r) => r.uuid !== report.uuid);
          this.totalReport -= 1;
        }
      } catch (error) {
        const extractedError = helper.extractAlert(error, "reports");
        this.$toast.error(extractedError.alert.message);
        if (extractedError.redirect) {
          this.userStore.signOut();
          this.$router.push({ name: extractedError.redirect });
        }
      } finally {
        this.activeReport = null;
        this.userStore.setIsLoading(!1);
      }
    },
    async reRunReport(report) {
      try {
        this.userStore.setIsLoading(!0);
        const url = `api/serp/search/${report.uuid}/rerun_search`;
        const { data } = await ApiRequest().get(url);
        if (data && data.detail) {
          this.$toast.success(data.detail);
        }
        this.showIsArchived = false;
        await new Promise((resolve) => setTimeout(resolve, 2000));
        this.loadReports();
      } catch (error) {
        const extractedError = helper.extractAlert(error, "reports");
        this.$toast.error(extractedError.alert.message);
        if (extractedError.redirect) {
          this.userStore.signOut();
          this.$router.push({ name: extractedError.redirect });
        }
      } finally {
        this.activeReport = null;
        this.userStore.setIsLoading(!1);
      }
    },
    changeShowIsArchived(val) {
      this.showIsArchived = !!val;
      this.loadReports();
    },
    isReportProcessing(report) {
      let isPendingOrProcessingReport = false;
      if (report.test_progress && report.test_progress.details) {
        for (const test in report.test_progress.details) {
          if (
            report.test_progress.details[test] === "processing" ||
            report.test_progress.details[test] === "Processing"
          )
            isPendingOrProcessingReport = true;
        }
      }
      return isPendingOrProcessingReport;
    },
    isReportFailed(report) {
      let isFailedReport = false;
      if (report.test_progress && report.test_progress.details) {
        for (const test in report.test_progress.details) {
          if (
            report.test_progress.details[test] === "failed" ||
            report.test_progress.details[test] === "Failed"
          )
            isFailedReport = true;
        }
      }
      return isFailedReport;
    },
    isPossiblyIncompleted(report) {
      let isPossiblyIncompleted = false;
      const startSpinningTime = new Date(report.created_at);
      const currentTime = new Date();
      const differenceInMinutes =
        (currentTime - startSpinningTime) / (1000 * 60);
      if (
        differenceInMinutes > 15 &&
        this.isReportProcessing(report) &&
        !this.isReportFailed(report)
      ) {
        isPossiblyIncompleted = true;
      }
      return isPossiblyIncompleted;
    },
    isPossiblyIncompletedSecondStep(report) {
      let isPossiblyIncompletedSecondStep = false;
      const startSpinningTime = new Date(report.created_at);
      const currentTime = new Date();
      const differenceInMinutes =
        (currentTime - startSpinningTime) / (1000 * 60);
      if (
        differenceInMinutes > 25 &&
        this.isReportProcessing(report) &&
        !this.isReportFailed(report)
      ) {
        isPossiblyIncompletedSecondStep = true;
      }
      return isPossiblyIncompletedSecondStep;
    },
    isReportRetrying(report) {
      let isRetryingReport = false;
      if (report.test_progress && report.test_progress.details) {
        for (const test in report.test_progress.details) {
          if (
            report.test_progress.details[test] === "retrying" ||
            report.test_progress.details[test] === "Retrying"
          )
            isRetryingReport = true;
        }
      }
      return isRetryingReport;
    },
    isSprayAndPray(obj) {
      if (!obj) return false;
      for (const key in obj) {
        if (obj[key].status !== 60) {
          return false;
        }
      }
      return true;
    },
  },
  watch: {
    selectedProject(val) {
      this.loadReportOrProject();
    },
    enteredReportSearchQuery(val) {
      this.loadReportOrProject();
    },
  },
};
</script>
<style src="@vueform/multiselect/themes/default.scss"></style>
